// ./src/components/generic/AppButton/AppButton.tsx
import React from "react";
import { Button, ButtonProps, Tooltip } from "@mantine/core";

import { AppIcon } from "../AppIcon";
import { LoadingIndicator } from "../LoadingIndicator";

import { useAppEnvironment } from "../AppEnvironmentContext";

// ============================================
// Type Definitions
// ============================================

type AppVariant = "primary" | "secondary" | "tertiary" | "neutral" | "danger";
type ButtonSize = "xs" | "sm" | "md" | "lg" | "xl";

export interface AppButtonProps
  extends Omit<
    ButtonProps,
    "children" | "leftSection" | "rightSection" | "variant"
  > {
  /** Button text content */
  children?: React.ReactNode;

  /** Alternative to children for button text */
  label?: string;

  /** Icon name (without 'Icon' prefix) to display */
  iconName?: string | undefined;

  /** Loading state - disables button and shows loading indicator */
  inProgress?: boolean;

  /** Button variant by purpose */
  variant?: AppVariant;

  /** Icon position when both text and icon are present */
  iconPosition?: "left" | "right";

  /** Size of the icon */
  iconSize?: number;

  type?: "button" | "submit" | "reset" | undefined;

  /** Click handler */
  onClick?: (() => void) | undefined;

  /** Tooltip text to display on hover */
  tooltip?: string;

  /** Tooltip position */
  tooltipPosition?: "top" | "right" | "bottom" | "left";

  hideOnMobile?: boolean;
}

// ============================================
// Mapping Objects (outside component for performance)
// ============================================

/** Maps app variants to Mantine button variants */
const MANTINE_VARIANT_MAP: Record<
  AppVariant,
  NonNullable<ButtonProps["variant"]>
> = {
  primary: "filled",
  secondary: "outline",
  tertiary: "subtle",
  // A neutral grey-bordered button. `secondary` is brand-coloured, which reads as a
  // second call-to-action — wrong for the "Cancel" beside a destructive confirm.
  neutral: "default",
  // FILLED, not subtle: a destructive confirm is the primary action of its dialog, so
  // it needs the weight of a solid button. As a subtle button it read as a text link
  // and sat visually *below* a brand-outlined Cancel.
  danger: "filled",
};

/** Maps app variants to theme colors */
const COLOR_MAP: Record<AppVariant, string> = {
  primary: "brand",
  secondary: "brand",
  tertiary: "brand",
  neutral: "gray",
  danger: "error",
};

/** Maps button sizes to icon sizes (in pixels) */
const ICON_SIZE_MAP: Record<ButtonSize, number> = {
  xs: 12,
  sm: 14,
  md: 16,
  lg: 18,
  xl: 20,
};

const DEFAULT_ICON_SIZE = 16;

// ============================================
// Component
// ============================================

export const AppButton: React.FC<AppButtonProps> = ({
  children,
  label,
  iconName,
  size = "md",
  inProgress = false,
  variant = "primary",
  iconPosition = "left",
  iconSize,
  disabled,
  onClick,
  type,
  tooltip,
  tooltipPosition = "top",
  hideOnMobile = false,
  ...buttonProps
}) => {
  const buttonText = children || label;
  const isDisabled = disabled || inProgress;
  const isIconOnly = iconName && !buttonText;

  // Derive values from maps
  const mantineVariant = MANTINE_VARIANT_MAP[variant];
  const buttonColor = COLOR_MAP[variant];
  const computedIconSize =
    iconSize ?? ICON_SIZE_MAP[size as ButtonSize] ?? DEFAULT_ICON_SIZE;
  const { isMobile } = useAppEnvironment();

  if (hideOnMobile && isMobile) {
    return null;
  }

  // Get the icon or loading indicator
  const iconElement = inProgress ? (
    <LoadingIndicator size={computedIconSize} />
  ) : iconName ? (
    <AppIcon iconName={iconName} size={computedIconSize} />
  ) : null;

  // Build section props
  const sectionProps = iconElement
    ? iconPosition === "left"
      ? { leftSection: iconElement }
      : { rightSection: iconElement }
    : {};

  // Common button props
  const commonProps = {
    size,
    variant: mantineVariant,
    color: buttonColor,
    disabled: isDisabled,
    type,
    onClick,
    ...buttonProps,
  };

  // Render button
  const button = isIconOnly ? (
    <Button {...commonProps}>{iconElement}</Button>
  ) : (
    <Button {...commonProps} {...sectionProps}>
      {buttonText}
    </Button>
  );

  // Wrap with tooltip if provided
  if (tooltip) {
    return (
      <Tooltip
        label={tooltip}
        position={tooltipPosition}
        withArrow
        openDelay={500}
      >
        {button}
      </Tooltip>
    );
  }

  return button;
};
